import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Target,
    Upload,
    RefreshCw,
    Pencil,
    Plus,
    Trash2,
    Loader2,
    Info,
    ArrowUpRight,
    ArrowDownRight,
    CircleSlash,
    Clock3,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { fmtNumber, currencySuffix } from "@/lib/format";
import ImportTargetsDialog from "@/components/sales/ImportTargetsDialog";

const B = OPS.salesBudget;

// last closed month (previous calendar month) as YYYY-MM
function defaultMonth() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const RAG_STYLE = {
    green: "bg-[color:rgba(31,122,77,0.14)] text-[color:var(--success)]",
    amber: "bg-[color:rgba(209,128,47,0.16)] text-[color:var(--brand-brown)]",
    red: "bg-[color:rgba(220,38,38,0.10)] text-[color:var(--danger)]",
};

function RagChip({ rag, t }) {
    if (!rag) return <span className="text-[color:var(--ink-faint)]">—</span>;
    return (
        <Badge variant="secondary" className={`border-0 ${RAG_STYLE[rag]}`}>
            {t(`budget_rag_${rag}`)}
        </Badge>
    );
}

function Money({ value, lang, status, muted }) {
    if (status === "no_target") {
        return <span className="text-xs text-[color:var(--ink-faint)] italic">—</span>;
    }
    if (status === "no_data") {
        return <span className="text-xs text-[color:var(--ink-faint)] italic">—</span>;
    }
    if (value === null || value === undefined) return <span className="text-[color:var(--ink-faint)]">—</span>;
    return (
        <span className={`tnum ${muted ? "text-[color:var(--ink-muted)]" : "text-[color:var(--ink)]"}`}>
            {fmtNumber(value, lang, 0)}
        </span>
    );
}

function VarianceCell({ row, lang }) {
    if (!row.comparable || row.variance === null) return <span className="text-[color:var(--ink-faint)]">—</span>;
    const pos = row.variance >= 0;
    return (
        <span className={`tnum inline-flex items-center gap-1 ${pos ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}`}>
            {pos ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
            {fmtNumber(Math.abs(row.variance), lang, 0)}
        </span>
    );
}

function StatusLabel({ row, t }) {
    // A single honest label when a cell can't be compared.
    if (row.target_status === "no_target") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ink-faint)]">
                <CircleSlash className="h-3.5 w-3.5" />
                {t("budget_no_target")}
            </span>
        );
    }
    if (row.actual_status === "no_data") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ink-faint)]">
                <CircleSlash className="h-3.5 w-3.5" />
                {t("budget_actual_missing")}
            </span>
        );
    }
    if (row.actual_status === "partial") {
        // month-to-date: shown, but never scored against a whole-month target
        return (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ink-muted)]" title={row.actual_through || ""}>
                <Clock3 className="h-3.5 w-3.5" />
                {t("budget_actual_mtd")}
            </span>
        );
    }
    return <RagChip rag={row.rag} t={t} />;
}

function SetTargetDialog({ open, onOpenChange, branch, month, current, onSaved }) {
    const { t, lang } = useI18n();
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setValue(current != null ? String(current) : "");
    }, [open, current]);

    const save = async () => {
        const num = Number(String(value).replace(/,/g, ""));
        if (!Number.isFinite(num) || num <= 0) {
            toast.error(t("budget_target_invalid"));
            return;
        }
        setBusy(true);
        try {
            await api.post("/sales-budget/targets", { branch_id: branch.branch_id, month, target: num });
            toast.success(t("budget_target_saved"));
            onSaved && onSaved();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        setBusy(true);
        try {
            await api.delete("/sales-budget/targets", { params: { branch_id: branch.branch_id, month } });
            toast.success(t("budget_target_cleared"));
            onSaved && onSaved();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    if (!branch) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={B.dialog} className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("budget_set_target")}
                    </DialogTitle>
                    <DialogDescription>
                        {lang === "ar" ? branch.branch_name_ar : branch.branch_name_en || branch.branch_name_ar}
                        {" · "}
                        <bdi dir="ltr">{month}</bdi>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-[color:var(--ink)]">
                        {t("budget_target_amount")} ({currencySuffix(lang)})
                    </label>
                    <Input
                        data-testid={B.dialogInput}
                        type="number"
                        min="1"
                        step="1"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="0"
                        dir="ltr"
                    />
                </div>
                <DialogFooter className="gap-2 sm:justify-between">
                    {current != null ? (
                        <Button data-testid={B.dialogClear} variant="outline" onClick={clear} disabled={busy}
                            className="text-[color:var(--danger)] border-[color:var(--danger-soft)] hover:bg-[color:rgba(220,38,38,0.06)]">
                            <Trash2 className="h-4 w-4 me-1.5" />
                            {t("budget_clear_target")}
                        </Button>
                    ) : <span />}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                            {t("cancel")}
                        </Button>
                        <Button data-testid={B.dialogSave} onClick={save} disabled={busy}
                            className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white">
                            {busy ? <Loader2 className="h-4 w-4 me-1.5 animate-spin" /> : null}
                            {t("save")}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SalesBudgetPage() {
    const { t, lang, tn } = useI18n();
    const [month, setMonth] = useState(defaultMonth());
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);

    const load = useCallback(async (m) => {
        setBusy(true);
        setError(null);
        try {
            const r = await api.get("/sales-budget/comparison", { params: { month: m } });
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || t("error_generic"));
            setData(null);
        } finally {
            setBusy(false);
        }
    }, [t]);

    useEffect(() => {
        if (/^\d{4}-\d{2}$/.test(month)) load(month);
    }, [month, load]);

    const coverageText = useMemo(() => {
        const c = data?.coverage;
        if (!c) return "";
        return t("budget_coverage_fmt")
            .replace("{comparable}", c.comparable)
            .replace("{with_target}", c.with_target)
            .replace("{total}", c.branches_total);
    }, [data, t]);

    const total = data?.total;

    return (
        <div data-testid={B.page} className="space-y-4">
            {/* controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--brand-orange-soft)]">
                        <Target className="h-4.5 w-4.5 text-[color:var(--brand-orange)]" />
                    </span>
                    <div>
                        <h2 className="text-base font-bold text-[color:var(--ink)]">{t("budget_title")}</h2>
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("budget_subtitle")}</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        data-testid={B.monthInput}
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="h-9 w-[150px]"
                        dir="ltr"
                    />
                    <Button data-testid={B.refresh} variant="soft" size="sm" onClick={() => load(month)} disabled={busy} className="gap-2">
                        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    </Button>
                    <Button data-testid={B.importBtn} size="sm" onClick={() => setImportOpen(true)}
                        className="gap-2 bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white">
                        <Upload className="h-3.5 w-3.5" />
                        {t("budget_import_targets")}
                    </Button>
                </div>
            </div>

            {/* compared-against clarity banner */}
            <div data-testid={B.comparedNote} className="flex items-start gap-2 rounded-lg surface-2 border border-[color:var(--border-soft)] px-3 py-2 text-xs text-[color:var(--ink-muted)]">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--brand-sky)]" />
                <span>
                    {t("budget_compared_against")}{" "}
                    <span className="font-semibold text-[color:var(--ink)]">
                        {data?.compared_against ? (lang === "ar" ? data.compared_against.ar : data.compared_against.en) : t("budget_revenue_gross")}
                    </span>
                    {data && !data.is_closed ? <span className="ms-1">· {t("budget_month_open")}</span> : null}
                </span>
            </div>

            {error ? (
                <div className="rounded-lg border border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.06)] p-3 text-sm text-[color:var(--danger)]">
                    {error}
                </div>
            ) : null}

            <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardContent className="p-0">
                    {data === null ? (
                        <div className="p-4"><Skeleton className="h-64 w-full" /></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={B.table}>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="text-start">{t("budget_col_branch")}</TableHead>
                                        <TableHead className="text-end">{t("budget_col_target")}</TableHead>
                                        <TableHead className="text-end">{t("budget_col_actual")}</TableHead>
                                        <TableHead className="text-end">{t("budget_col_variance")}</TableHead>
                                        <TableHead className="text-end">{t("budget_col_variance_pct")}</TableHead>
                                        <TableHead className="text-center">{t("budget_col_status")}</TableHead>
                                        <TableHead className="text-center w-[52px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.rows.map((r) => (
                                        <TableRow key={r.branch_id} data-testid={B.row(r.branch_id)}>
                                            <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                                {tn({ name_ar: r.branch_name_ar, name_en: r.branch_name_en })}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                {r.target_status === "no_target" ? (
                                                    <span className="text-xs text-[color:var(--ink-faint)] italic">{t("budget_no_target")}</span>
                                                ) : (
                                                    <Money value={r.target} lang={lang} muted />
                                                )}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                {r.actual_status === "no_data" ? (
                                                    <span className="text-xs text-[color:var(--ink-faint)] italic">{t("budget_actual_missing")}</span>
                                                ) : (
                                                    <Money value={r.actual} lang={lang} />
                                                )}
                                            </TableCell>
                                            <TableCell className="text-end"><VarianceCell row={r} lang={lang} /></TableCell>
                                            <TableCell className="text-end tnum">
                                                {r.comparable && r.variance_pct !== null ? (
                                                    <span className={r.variance_pct >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"} dir="ltr">
                                                        {r.variance_pct >= 0 ? "+" : ""}{fmtNumber(r.variance_pct, lang, 1)}%
                                                    </span>
                                                ) : <span className="text-[color:var(--ink-faint)]">—</span>}
                                            </TableCell>
                                            <TableCell className="text-center" data-testid={B.rag(r.branch_id)}>
                                                <StatusLabel row={r} t={t} />
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Button
                                                    data-testid={B.setTargetBtn(r.branch_id)}
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 text-[color:var(--ink-muted)] hover:text-[color:var(--brand-orange)]"
                                                    onClick={() => setEditRow(r)}
                                                    title={r.target_status === "no_target" ? t("budget_set_target") : t("budget_edit_target")}
                                                >
                                                    {r.target_status === "no_target" ? <Plus className="h-4 w-4" /> : <Pencil className="h-3.5 w-3.5" />}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Company total (like-for-like over comparable branches) */}
                                    {total ? (
                                        <TableRow data-testid={B.totalRow} className="bg-[color:rgba(224,222,217,0.55)] font-semibold hover:bg-[color:rgba(224,222,217,0.55)]">
                                            <TableCell className="text-[color:var(--ink)] whitespace-nowrap">{t("budget_company_total")}</TableCell>
                                            <TableCell className="text-end">
                                                {total.target_status === "no_target" ? <span className="text-xs text-[color:var(--ink-faint)] italic">{t("budget_no_target")}</span> : <Money value={total.target} lang={lang} muted />}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                {total.actual_status === "no_data" ? <span className="text-xs text-[color:var(--ink-faint)] italic">{t("budget_actual_missing")}</span> : <Money value={total.actual} lang={lang} />}
                                            </TableCell>
                                            <TableCell className="text-end"><VarianceCell row={total} lang={lang} /></TableCell>
                                            <TableCell className="text-end tnum">
                                                {total.comparable && total.variance_pct !== null ? (
                                                    <span className={total.variance_pct >= 0 ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"} dir="ltr">
                                                        {total.variance_pct >= 0 ? "+" : ""}{fmtNumber(total.variance_pct, lang, 1)}%
                                                    </span>
                                                ) : <span className="text-[color:var(--ink-faint)]">—</span>}
                                            </TableCell>
                                            <TableCell className="text-center"><RagChip rag={total.rag} t={t} /></TableCell>
                                            <TableCell />
                                        </TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {data ? (
                <p data-testid={B.coverage} className="text-xs text-[color:var(--ink-muted)] px-1">{coverageText}</p>
            ) : null}

            <ImportTargetsDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => load(month)} />
            <SetTargetDialog
                open={!!editRow}
                onOpenChange={(v) => { if (!v) setEditRow(null); }}
                branch={editRow}
                month={month}
                current={editRow?.target ?? null}
                onSaved={() => load(month)}
            />
        </div>
    );
}
